import React, { useEffect, useState } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import CoverageStrip from "./CoverageStrip";
import {
  calculateCoverage,
  createMaterials,
  formatDate,
  formatQuantity,
  getDate,
  getDaysDifference,
  getMaterial,
  getPOBalance,
  getPOStatus,
  getPRStatus,
} from "../../utils/reportUtils";


// File name and upload time of the most recent upload among rows that record their source file.
const getLatestSource = (rows) => {
  let latest = null;
  rows.forEach((row) => {
    if (!row.sourceUploadedAt) return;
    if (
      !latest ||
      new Date(row.sourceUploadedAt) > new Date(latest.sourceUploadedAt)
    ) {
      latest = row;
    }
  });
  return {
    fileName: latest?.sourceFileName || null,
    uploadedAt: latest?.sourceUploadedAt || null,
  };
};

const Dashboard = () => {
  const navigate = useNavigate();
  const { getReports } = useOutletContext();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [mrp, setMrp] = useState([]);
  const [stock, setStock] = useState([]);
  const [prs, setPrs] = useState([]);
  const [pos, setPos] = useState([]);
  const [productionAdjustments, setProductionAdjustments] = useState([]);
  const [latestUpload, setLatestUpload] = useState(null);
  const [deliveries, setDeliveries] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [results, setResults] = useState([]);

  // India date, not UTC, so "today" is correct between 00:00 and 05:30 IST.
  const today = getDate(new Date());

  useEffect(() => {
    loadDashboard();
  }, []);

  const loadDashboard = async () => {
    setLoading(true);
    setError(null);

    try {
      // Always loaded fresh here; Shortages and Materials reuse this copy (see ProcureTrackLayout).
      const { m, s, p, o, prd, u, d, alt, uploadError } = await getReports(true);
      // The latest upload is required on the dashboard.
      if (uploadError) {
        throw uploadError;
      }

      const mrpData = m.data || [];
      const stockData = s.data || [];
      const prData = p.data || [];
      const poData = o.data || [];
      const productionData = prd.data || [];

      setMrp(mrpData);
      setStock(stockData);
      setPrs(prData);
      setPos(poData);
      setProductionAdjustments(productionData);
      setLatestUpload(u.data);
      setDeliveries(d.data || []);

      setMaterials(
        createMaterials(mrpData, stockData, prData, poData, productionData),
      );
      setResults(
        calculateCoverage(
          mrpData,
          stockData,
          prData,
          poData,
          today,
          productionData,
          alt.data || [],
          // Receipts after the stock date are added to stock.
          d.data || [],
          u.data?.uploadDate || u.data?.createdDate,
          u.data?.uploadDate || u.data?.createdDate,
        ),
      );
    } catch (e) {
      setError(
        e.response?.data?.Exception ||
          e.response?.data?.message ||
          "Could not load dashboard data.",
      );
    } finally {
      setLoading(false);
    }
  };

  const shortCount = results.filter(
    (result) => result.status === "SHORT",
  ).length;

  const riskCount = results.filter(
    (result) => result.status === "AT_RISK",
  ).length;

  const coveredCount = results.filter(
    (result) => result.status === "COVERED",
  ).length;

  const openPrLines = prs.filter(
    (pr) => getPRStatus(pr, pos) === "OPEN",
  ).length;

  const closedPrLines = prs.filter(
    (pr) => getPRStatus(pr, pos) === "CLOSED",
  ).length;

  const overdue = pos.filter(
    (po) => getPOStatus(po, today) === "OVERDUE",
  ).length;

  const dueSoon = pos.filter(
    (po) => getPOStatus(po, today) === "DUE_SOON",
  ).length;

  const poWithBalance = pos.filter((po) => getPOBalance(po) > 0).length;

  const nextShortages = results
    .filter((result) => result.firstShortageDate)
    .sort((a, b) => {
      if (a.firstShortageDate < b.firstShortageDate) {
        return -1;
      }

      if (a.firstShortageDate > b.firstShortageDate) {
        return 1;
      }

      return 0;
    })
    .slice(0, 7);

  const goToShortages = (status) => {
    navigate("../shortages", {
      state: {
        status: status,
      },
    });
  };

  if (loading) {
    return (
      <div className="page">
        <div className="empty">Loading dashboard...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page">
        <div className="note err">
          <b>Couldn't load data.</b> {error}
        </div>
      </div>
    );
  }

  const hasData =
    mrp.length ||
    stock.length ||
    prs.length ||
    pos.length ||
    productionAdjustments.length;

  if (!hasData) {
    return (
      <div className="page">
        <div className="empty">
          <strong>No reports uploaded yet.</strong>

          <div>
            Upload an MRP, Stock, Open PR or Open PO report to see coverage
            here.
          </div>

          <div
            style={{
              marginTop: 14,
            }}
          >
            <button className="btn btn-p" onClick={() => navigate("../upload")}>
              Go to Upload reports
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page">
      <div className="ph">
        <div>
          <h1>Coverage dashboard</h1>

          <p>
            Netting run <strong>{formatDate(today)}</strong> ·{" "}
            {materials.length} materials
          </p>
        </div>
      </div>

      <div className="tiles">
        <button className="tile t-short" onClick={() => goToShortages("SHORT")}>
          <div className="tile-l">Short</div>

          <div className="tile-v">{shortCount}</div>

          <div className="tile-s">not enough on the way</div>
        </button>

        <button
          className="tile t-risk"
          onClick={() => goToShortages("AT_RISK")}
        >
          <div className="tile-l">At risk</div>

          <div className="tile-v">{riskCount}</div>

          <div className="tile-s">covered, arriving late</div>
        </button>

        <button className="tile t-ok" onClick={() => goToShortages("COVERED")}>
          <div className="tile-l">Covered</div>

          <div className="tile-v">{coveredCount}</div>

          <div className="tile-s">no action needed</div>
        </button>

        <button
          className="tile t-info"
          onClick={() => navigate("../requisitions", { state: { preset: "OPEN" } })}
        >
          <div className="tile-l">Open PR lines</div>
          <div className="tile-v">{openPrLines}</div>
          <div className="tile-s">no PO associated</div>
        </button>
      </div>

      <div className="tiles">
        <button
          className="tile t-mut"
          onClick={() => navigate("../requisitions", { state: { preset: "CLOSED" } })}
        >
          <div className="tile-l">Closed PR lines</div>
          <div className="tile-v">{closedPrLines}</div>
          <div className="tile-s">PO associated</div>
        </button>

        <button
          className="tile t-short"
          onClick={() =>
            navigate("../purchase-orders", {
              state: {
                preset: "OVERDUE",
              },
            })
          }
        >
          <div className="tile-l">POs overdue</div>

          <div className="tile-v">{overdue}</div>

          <div className="tile-s">delivery date passed</div>
        </button>

        <button
          className="tile t-risk"
          onClick={() =>
            navigate("../purchase-orders", {
              state: {
                preset: "DUE_SOON",
              },
            })
          }
        >
          <div className="tile-l">POs due in 7 days</div>

          <div className="tile-v">{dueSoon}</div>

          <div className="tile-s">confirm with vendor</div>
        </button>

        <button
          className="tile t-mut"
          onClick={() => navigate("../requisitions", { state: { preset: "OPEN" } })}
        >
          <div className="tile-l">Open PR lines</div>

          <div className="tile-v">{openPrLines}</div>

          <div className="tile-s">in the pipeline</div>
        </button>
      </div>

      <div className="card">
        <div className="card-h">
          <div>
            <div className="card-t">Next shortages</div>

            <div className="card-s">
              Earliest date the running balance turns negative
            </div>
          </div>

          <button
            className="btn btn-o btn-sm"
            onClick={() => navigate("../shortages")}
          >
            Open workbench
          </button>
        </div>

        <div className="tw">
          <table className="dt">
            <thead>
              <tr>
                <th>Status</th>
                <th>Material</th>
                <th>Description</th>
                <th>10-week coverage</th>
                <th className="r">Shortage</th>
                <th>First shortage</th>
              </tr>
            </thead>

            <tbody>
              {nextShortages.length > 0 ? (
                nextShortages.map((result) => {
                  const material = getMaterial(materials, result.key);

                  const days = getDaysDifference(
                    result.firstShortageDate,
                    today,
                  );

                  return (
                    <tr
                      key={result.key}
                      onClick={() => navigate(`../shortages/${result.key}`)}
                      className="clk"
                    >
                      <td>
                        <span
                          className={`bg ${
                            result.status === "COVERED"
                              ? "b-ok"
                              : result.status === "AT_RISK"
                                ? "b-risk"
                                : "b-short"
                          }`}
                        >
                          {result.status === "COVERED"
                            ? "Covered"
                            : result.status === "AT_RISK"
                              ? "At risk"
                              : "Short"}
                        </span>
                      </td>
                      <td className="mono">{material?.code}</td>
                      <td>{material?.desc}</td>
                      <td>
                        <CoverageStrip result={result} today={today} />
                      </td>
                      {/* Same as the shortage workbench: "—" when the balance recovers (at risk). */}
                      <td className={`num ${result.shortageQty > 0 ? "" : "dim"}`}>
                        {result.shortageQty > 0
                          ? `${formatQuantity(result.shortageQty)} ${material?.uom || ""}`
                          : "—"}
                      </td>
                      <td>
                        {formatDate(result.firstShortageDate)}
                        <span className="dim">
                          {" "}
                          · {days <= 0 ? "now" : `in ${days}d`}
                        </span>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={6} className="empty">
                    <strong>Everything is covered.</strong> No material goes
                    negative in the planning horizon.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="grid2">
        <div className="card">
          <div className="card-h">
            <div className="card-t">Where the pipeline is stuck</div>
          </div>

          <div className="card-b">
            <StuckRow
              label="Open PR lines"
              count={openPrLines}
              total={prs.length}
              note="No purchase order is associated with these requisitions."
            />

            <StuckRow
              label="Closed PR lines"
              count={closedPrLines}
              total={prs.length}
              note="A purchase order is associated with these requisitions."
            />

            <StuckRow
              label="POs past delivery date"
              count={overdue}
              total={poWithBalance}
              note="Vendor has missed the committed date."
            />
          </div>
        </div>

        <div className="card">
          <div className="card-h">
            <div className="card-t">Active snapshot</div>
          </div>

          <div className="card-b">
            {/* Which file each report currently comes from, and when it was uploaded. */}
            {[
              {
                label: "MRP & Stock",
                fileName: latestUpload?.fileName,
                uploadedAt: latestUpload?.uploadDate || latestUpload?.createdDate,
              },
              { label: "Open PR", ...getLatestSource(prs) },
              { label: "Open PO", ...getLatestSource(pos) },
              { label: "Delivery", ...getLatestSource(deliveries) },
            ].map((report) => (
              <div className="kv" key={report.label}>
                <span className="kv-k">{report.label}</span>

                <span className="kv-v">
                  {report.fileName || report.uploadedAt ? (
                    <>
                      <span className="mono">{report.fileName || "—"}</span>
                      {report.uploadedAt &&
                        ` · ${formatDate(getDate(report.uploadedAt))}`}
                    </>
                  ) : (
                    "Not uploaded"
                  )}
                </span>
              </div>
            ))}

            <div
              style={{
                marginTop: 12,
              }}
            >
              <button
                className="btn btn-o btn-sm"
                onClick={() => navigate("../snapshots")}
              >
                Snapshot history
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const StuckRow = ({ label, count, total, note }) => {
  const percentage = total > 0 ? Math.round((count / total) * 100) : 0;

  return (
    <div
      style={{
        marginBottom: 14,
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "baseline",
          gap: 10,
        }}
      >
        <span
          style={{
            fontWeight: 500,
          }}
        >
          {label}
        </span>

        <span
          className="mono"
          style={{
            fontSize: 12,
          }}
        >
          {count}

          <span className="dim"> / {total}</span>
        </span>
      </div>

      <div
        className="bar"
        style={{
          margin: "6px 0 4px",
        }}
      >
        <i
          style={{
            width: `${percentage}%`,
          }}
        ></i>
      </div>

      <div
        className="dim"
        style={{
          fontSize: 11.5,
        }}
      >
        {note}
      </div>
    </div>
  );
};

export default Dashboard;
